<template>
  <v-dialog :retain-focus="false" :value="sectionEditor.show" :width="1000" @input="handleClose">
    <div style="background-color: #fff">
      <div class="header-modal">
        <v-card-title class="d-flex justify-space-between align-center" style="padding: 10px 0">
          <span style="color: #0081c7">Card Side by Side Media</span>
          <v-btn icon @click="handleClose">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
      </div>

      <v-card-text style="max-height: 70vh; overflow-y: auto">
        <!-- Section Settings -->
        <div class="mb-4">
          <SectionSettingsContainerFuild
            :use-background-mobile="true"
            :settings="fields.settings"
            :container-page="fields.containerPage"
          />
        </div>

        <!-- LEFT MEDIA GROUP -->
        <div class="mb-4">
          <label class="label-input">Left Media</label>
          <div class="group-item">
            <!-- Media Type & Position -->
            <v-row class="mb-3">
              <v-col cols="12" md="6">
                <label style="display: block; font-weight: 500; margin-bottom: 8px">Media Type</label>
                <v-radio-group v-model="fields.mediaType" row>
                  <v-radio label="Video" value="video" />
                  <v-radio label="Image" value="image" />
                </v-radio-group>
              </v-col>
              <v-col cols="12" md="6">
                <label style="display: block; font-weight: 500; margin-bottom: 8px">Position</label>
                <v-radio-group v-model="fields.mediaPosition" row>
                  <v-radio label="Left" value="left" />
                  <v-radio label="Right" value="right" />
                </v-radio-group>
              </v-col>
            </v-row>

            <!-- Center Y -->
            <v-checkbox v-model="fields.centerY" label="Center Y" class="mb-3" />

            <!-- Video URL -->
            <div v-if="fields.mediaType === 'video'" class="mb-3">
              <label style="display: block; font-weight: 500; margin-bottom: 8px">Video URL</label>
              <v-text-field v-model="fields.video.src" type="url" placeholder="Enter video URL" dense />
            </div>

            <!-- Image Upload -->
            <div v-if="fields.mediaType === 'image'">
              <label style="display: block; font-weight: 500; margin-bottom: 8px">Image</label>
              <UploadImage
                :image="{ imageSrc: fields.image.src || '' }"
                :height="`300`"
                :typeUpload="'image'"
                @callback="handleImageUpdate"
                @callbackDelete="handleImageRemove"
              />
            </div>
          </div>
        </div>

        <!-- RIGHT CONTENT GROUP -->
        <div class="mb-4">
          <label class="label-input">Right Content</label>
          <div class="group-item">
            <!-- Heading Text -->
            <div class="mb-4">
              <div class="d-flex align-center justify-space-between mb-2">
                <label class="label-input mb-0">Heading & Description</label>
                <v-checkbox v-model="fields.headingText.show" label="Show" hide-details class="mt-0" />
              </div>
              <ckeditor :editor="editor" v-model="fields.headingText.content" :config="editorConfig"></ckeditor>
            </div>

            <!-- Rating Block -->
            <div class="mb-4">
              <div class="d-flex align-center justify-space-between mb-2">
                <label class="label-input mb-0">Social Rating Block</label>
                <v-checkbox v-model="fields.ratingBlock.show" label="Show" hide-details class="mt-0" />
              </div>

              <div v-if="fields.ratingBlock.show">
                <!-- Colors -->
                <div class="mb-3">
                  <v-row>
                    <v-col cols="12" md="6">
                      <div class="d-flex align-center gap-3">
                        <input
                          type="color"
                          style="width: 36px; height: 36px; margin-right: 8px"
                          :value="fields.ratingBlock.colorText"
                          @change="e => (fields.ratingBlock.colorText = e.target.value)"
                        />
                        <v-text-field v-model="fields.ratingBlock.colorText" label="Text Color" dense hide-details />
                      </div>
                    </v-col>
                    <v-col cols="12" md="6">
                      <div class="d-flex align-center gap-3">
                        <input
                          type="color"
                          style="width: 36px; height: 36px; margin-right: 8px"
                          :value="fields.ratingBlock.backgroundColor"
                          @change="e => (fields.ratingBlock.backgroundColor = e.target.value)"
                        />
                        <v-text-field
                          v-model="fields.ratingBlock.backgroundColor"
                          label="Background Color"
                          dense
                          hide-details
                        />
                      </div>
                    </v-col>
                  </v-row>
                </div>

                <!-- Rating Items -->
                <div>
                  <div class="d-flex justify-space-between align-center mb-2">
                    <span style="font-weight: 600">Rating Items</span>
                    <v-btn small text color="primary" @click="handleAddRatingItem">
                      <v-icon size="16">mdi-plus</v-icon>
                      <span>Add Item</span>
                    </v-btn>
                  </div>

                  <v-expansion-panels accordion>
                    <v-expansion-panel v-for="(item, index) in fields.ratingBlock.items" :key="`rating-${index}`">
                      <v-expansion-panel-header>
                        <div class="d-flex justify-space-between align-center" style="width: 100%">
                          <span>{{ item.platformName || `Item ${index + 1}` }}</span>
                          <v-btn
                            icon
                            small
                            :disabled="fields.ratingBlock.items.length <= 1"
                            @click.stop="handleRemoveRatingItem(index)"
                          >
                            <v-icon size="18" color="#ff6c6c">mdi-delete</v-icon>
                          </v-btn>
                        </div>
                      </v-expansion-panel-header>
                      <v-expansion-panel-content>
                        <v-text-field v-model="item.platformName" label="Platform Name" dense class="mb-3" />
                        <v-text-field v-model="item.ratingText" label="Rating Text" dense class="mb-3" />
                        <v-text-field
                          v-model.number="item.stars"
                          type="number"
                          label="Star Rating"
                          min="1"
                          max="5"
                          dense
                          class="mb-3"
                        />
                        <div>
                          <div class="d-flex align-center justify-space-between mb-2">
                            <label style="font-weight: 500">Image</label>
                            <v-checkbox v-model="item.showImage" label="Show" hide-details class="mt-0" />
                          </div>
                          <UploadImage
                            :image="{ imageSrc: item.imageSrc || '' }"
                            :height="`150`"
                            :typeUpload="'image'"
                            @callback="obj => handleRatingImageUpdate(obj, index)"
                            @callbackDelete="() => handleRatingImageRemove(index)"
                          />
                        </div>
                      </v-expansion-panel-content>
                    </v-expansion-panel>
                  </v-expansion-panels>
                </div>
              </div>
            </div>

            <!-- Button -->
            <div>
              <ButtonPrimary :button="fields.button" label="Button" />
            </div>
          </div>
        </div>
      </v-card-text>

      <v-card-actions class="pa-4">
        <v-spacer></v-spacer>
        <v-btn color="secondary" @click="handleClose">Cancel</v-btn>
        <v-btn color="primary" @click="handleSave">Save</v-btn>
      </v-card-actions>
    </div>
  </v-dialog>
</template>

<script>
import Editor from 'ckeditor5-custom-build/build/ckeditor';
import { ckeditorConfigFullToolbar } from '@/helpers/editor';
import UploadImage from '@/components/form/uploadFile/UpdateImage.vue';
import ButtonPrimary from './common/button-primary.vue';
import SectionSettingsContainerFuild from './common/section-settings-container-fuild.vue';
import { updateHtmlFromData } from '../common/html/update';

export default {
  name: 'card-cedrow-side-by-side-section-editor',
  components: {
    UploadImage,
    ButtonPrimary,
    SectionSettingsContainerFuild,
  },
  props: {
    sectionEditor: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      editor: Editor,
      editorConfig: ckeditorConfigFullToolbar,
      fields: {
        settings: {
          paddingTop: true,
          paddingBottom: true,
          backgroundColor: '#ffffff',
          textColor: '#000000',
          overlayColor: '#000000',
          overlayOpacity: 0,
          showOverlay: false,
          backgroundImage: '',
          useBackgroundMobile: false,
          backgroundImageMobile: '',
        },
        containerPage: {
          enableStyle: false,
          background: '#FFFFFF',
        },
        mediaType: 'video',
        mediaPosition: 'left',
        centerY: true,
        video: { src: '' },
        image: { src: '' },
        headingText: { show: true, content: '' },
        ratingBlock: {
          show: true,
          colorText: '#000000',
          backgroundColor: '#f9fbff',
          items: [
            { platformName: 'Facebook', showImage: false, imageSrc: '', ratingText: 'Rating 4.9', stars: 4 },
            { platformName: 'Google', showImage: false, imageSrc: '', ratingText: 'Rating 4.9', stars: 5 },
            { platformName: 'Cedrow', showImage: false, imageSrc: '', ratingText: 'Rating 4.9', stars: 3 },
          ],
        },
        button: {},
      },
    };
  },
  watch: {
    sectionEditor: {
      handler() {
        this.applyExtract();
      },
      deep: true,
    },
  },
  mounted() {
    this.applyExtract();
  },
  methods: {
    applyExtract() {
      if (this.sectionEditor?.sectionExtract) {
        const extract = this.sectionEditor.sectionExtract;
        this.fields = {
          settings: { ...this.fields.settings, ...(extract.settings || {}) },
          containerPage: { ...this.fields.containerPage, ...(extract.containerPage || {}) },
          mediaType: extract.mediaType || 'video',
          mediaPosition: extract.mediaPosition || 'left',
          centerY: extract.centerY,
          video: { src: extract.video?.src || '' },
          image: { src: extract.image?.src || '' },
          headingText: {
            show: extract.headingText?.show,
            content: extract.headingText?.content || '',
          },
          ratingBlock: {
            show: extract.ratingBlock?.show,
            colorText: extract.ratingBlock?.colorText || '#000000',
            backgroundColor: extract.ratingBlock?.backgroundColor || '#f9fbff',
            items: (extract.ratingBlock?.items || []).map(item => ({
              platformName: item.platformName || '',
              showImage: item.showImage,
              imageSrc: item.imageSrc || '',
              ratingText: item.ratingText || '',
              stars: item.stars || 5,
            })),
          },
          button: { ...extract.button },
        };
      }
    },
    handleImageUpdate(obj) {
      if (obj && obj.imageSrc) {
        this.fields.image.src = obj.imageSrc;
      }
    },
    handleImageRemove() {
      this.fields.image.src = '';
    },
    handleAddRatingItem() {
      this.fields.ratingBlock.items.push({
        platformName: 'New Platform',
        showImage: false,
        imageSrc: '',
        ratingText: 'Rating 0',
        stars: 5,
      });
    },
    handleRemoveRatingItem(index) {
      this.fields.ratingBlock.items.splice(index, 1);
    },
    handleRatingImageUpdate(obj, index) {
      if (obj && obj.imageSrc) {
        this.fields.ratingBlock.items[index].imageSrc = obj.imageSrc;
      }
    },
    handleRatingImageRemove(index) {
      this.fields.ratingBlock.items[index].imageSrc = '';
    },
    handleClose() {
      this.$emit('close');
    },
    handleSave() {
      const { section } = this.sectionEditor;
      const html = updateHtmlFromData.sectionCardCedrowSideBySide(section.html, this.fields);
      this.$emit('update:section', {
        ...section,
        html,
        title: 'Card Side by Side Media',
      });
      this.$emit('close');
    },
  },
};
</script>

<style scoped>
.header-modal {
  border-bottom: 1px solid #e0e0e0;
  padding: 10px 20px;
}

.label-input {
  display: block;
  font-weight: 600;
  margin-bottom: 12px;
  color: #333;
}

.group-item {
  /* background: #f5f5f5; */
  padding: 12px;
  border-radius: 4px;
}
</style>
